import React, { createContext, useContext, useState, useEffect } from 'react';
import { orderAPI } from '../services/api';
import { productAPI } from '../services/api';
import { userAPI } from '../services/api';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

const emptyAnalytics = {
  sales: { totalRevenue: 0, totalOrders: 0, averageOrderValue: 0, conversionRate: 0, monthlyGrowth: 0, dailySales: [], topProducts: [], revenueByCategory: [] },
  customers: { totalCustomers: 0, newCustomers: 0, returningCustomers: 0, customerGrowth: 0, averageCustomerValue: 0, customerSegments: [], topCustomers: [] },
  inventory: { totalValue: 0, lowStockItems: 0, outOfStockItems: 0, turnoverRate: 0, averageStockDays: 0, topMovingProducts: [] },
  marketing: { totalVisitors: 0, uniqueVisitors: 0, bounceRate: 0, averageSessionDuration: '0:00', trafficSources: [], conversionFunnel: [] }
};

export const AnalyticsProvider = ({ children }) => {
  const [analytics, setAnalytics] = useState(emptyAnalytics);
  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('7d');
  const [selectedMetric, setSelectedMetric] = useState('revenue');

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const [orderRes, productRes, userRes] = await Promise.all([
        orderAPI.getOrderStatistics().catch(() => ({ data: {} })),
        productAPI.getProductStatistics().catch(() => ({ data: {} })),
        userAPI.getUserStatistics().catch(() => ({ data: {} }))
      ]);
      const orderStats = orderRes.data?.data ?? orderRes.data ?? {};
      const productStats = productRes.data?.data ?? productRes.data ?? {};
      const userStats = userRes.data?.data ?? userRes.data ?? {};
      const totalRevenue = Number(orderStats.totalRevenue ?? 0);
      const totalOrders = Number(orderStats.totalOrders ?? 0);
      const avgOrder = Number(orderStats.averageOrderValue ?? 0);
      const totalCustomers = Number(userStats.totalUsers ?? userStats.total ?? 0);
      setAnalytics(prev => ({
        ...prev,
        sales: {
          ...prev.sales,
          totalRevenue,
          totalOrders,
          averageOrderValue: avgOrder,
          conversionRate: totalCustomers > 0 ? Math.round((totalOrders / totalCustomers) * 1000) / 10 : 0
        },
        customers: {
          ...prev.customers,
          totalCustomers
        },
        inventory: {
          ...prev.inventory,
          lowStockItems: Number(productStats.lowStockProducts ?? 0),
          outOfStockItems: Number(productStats.totalProducts ?? 0) - Number(productStats.inStockProducts ?? 0)
        }
      }));
    } catch (e) {
      console.error('Error loading analytics:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // İstatistik uçları yalnızca ADMIN/SUPER_ADMIN için yetkilidir; herkese açık
    // sayfalarda (misafir kullanıcıda) çağrılırsa sürekli 403 üretir.
    try {
      const savedUser = JSON.parse(localStorage.getItem('anadolu-user') || 'null');
      if (savedUser?.role === 'admin' && localStorage.getItem('authToken')) {
        loadAnalytics();
      }
    } catch {
      // ignore malformed storage
    }
  }, []);

  const updateDateRange = async (newRange) => {
    setDateRange(newRange);
    await loadAnalytics();
  };

  const getSalesData = () => {
    return analytics.sales;
  };

  const getCustomerData = () => {
    return analytics.customers;
  };

  const getInventoryData = () => {
    return analytics.inventory;
  };

  const getMarketingData = () => {
    return analytics.marketing;
  };

  const getRevenueChartData = () => {
    return analytics.sales.dailySales.map(day => ({
      date: day.date,
      revenue: day.revenue,
      orders: day.orders
    }));
  };

  const getTopProductsData = () => {
    return analytics.sales.topProducts;
  };

  const getRevenueByCategoryData = () => {
    return analytics.sales.revenueByCategory;
  };

  const getCustomerSegmentsData = () => {
    return analytics.customers.customerSegments;
  };

  const getTrafficSourcesData = () => {
    return analytics.marketing.trafficSources;
  };

  const getConversionFunnelData = () => {
    return analytics.marketing.conversionFunnel;
  };

  const getTopMovingProductsData = () => {
    return analytics.inventory.topMovingProducts;
  };

  const generateReport = async (reportType, format = 'pdf') => {
    setIsLoading(true);
    try {
      const reportPayload = { type: reportType, dateRange, generatedAt: new Date().toISOString(), data: analytics };
      const blob = new Blob([JSON.stringify(reportPayload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapor-${reportType}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      return { success: true, message: 'Rapor indirildi (JSON).' };
    } catch (error) {
      console.error('Report generation error:', error);
      return { success: false, message: 'Rapor oluşturulurken hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  const exportData = async (dataType, format = 'csv') => {
    setIsLoading(true);
    try {
      let payload = analytics;
      if (dataType === 'sales') payload = analytics.sales;
      else if (dataType === 'customers') payload = analytics.customers;
      else if (dataType === 'inventory') payload = analytics.inventory;
      else if (dataType === 'marketing') payload = analytics.marketing;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${dataType}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      return { success: true, message: 'Veri indirildi (JSON).' };
    } catch (error) {
      console.error('Export error:', error);
      return { success: false, message: 'Veri dışa aktarılırken hata oluştu' };
    } finally {
      setIsLoading(false);
    }
  };

  const getKPIs = () => {
    return {
      revenue: analytics.sales.totalRevenue,
      orders: analytics.sales.totalOrders,
      customers: analytics.customers.totalCustomers,
      conversionRate: analytics.sales.conversionRate,
      averageOrderValue: analytics.sales.averageOrderValue,
      customerGrowth: analytics.customers.customerGrowth,
      inventoryValue: analytics.inventory.totalValue,
      turnoverRate: analytics.inventory.turnoverRate
    };
  };

  const getTrends = () => {
    return {
      revenueGrowth: analytics.sales.monthlyGrowth,
      orderGrowth: 8.2,
      customerGrowth: analytics.customers.customerGrowth,
      inventoryGrowth: -2.1
    };
  };

  const value = {
    analytics,
    isLoading,
    dateRange,
    selectedMetric,
    setSelectedMetric,
    updateDateRange,
    getSalesData,
    getCustomerData,
    getInventoryData,
    getMarketingData,
    getRevenueChartData,
    getTopProductsData,
    getRevenueByCategoryData,
    getCustomerSegmentsData,
    getTrafficSourcesData,
    getConversionFunnelData,
    getTopMovingProductsData,
    generateReport,
    exportData,
    getKPIs,
    getTrends
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};
